import { useEffect, useState } from 'react';
import type { ProductView, Role } from '@medistock/contracts';
import { ApiError, api, type AdminUser, type ProductInput } from '../api';

/**
 * Setup surface (spec §20 P0): the product catalogue and the staff roster.
 * Both have to exist before a single vial can be received, so this is the first
 * screen an owner uses on a new install.
 */
export function Admin({
  role,
  currentUserId,
  onNotice,
  reload,
}: {
  role: Role;
  currentUserId: number;
  onNotice: (m: string) => void;
  reload: () => void;
}) {
  const [section, setSection] = useState<'products' | 'people'>('products');
  const isOwner = role === 'OWNER';

  return (
    <div className="list" data-testid="admin">
      <div className="row">
        <button
          className={section === 'products' ? 'primary grow' : 'grow'}
          data-testid="admin-tab-products"
          onClick={() => setSection('products')}
        >
          Products
        </button>
        <button
          className={section === 'people' ? 'primary grow' : 'grow'}
          data-testid="admin-tab-people"
          onClick={() => setSection('people')}
          disabled={!isOwner}
        >
          People
        </button>
      </div>

      {section === 'products' ? (
        <Products canEdit={isOwner} onNotice={onNotice} reload={reload} />
      ) : (
        <People currentUserId={currentUserId} onNotice={onNotice} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

const EMPTY_PRODUCT: ProductInput = {
  name: '',
  brand: '',
  unit: 'Unit',
  precision: 0,
  minStep: 1,
  vialSize: 100,
  quickOptions: [],
  reorderLevel: 0,
};

function Products({
  canEdit,
  onNotice,
  reload,
}: {
  canEdit: boolean;
  onNotice: (m: string) => void;
  reload: () => void;
}) {
  const [products, setProducts] = useState<(ProductView & { id: number })[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: number | null; value: ProductInput } | null>(null);

  const load = () => {
    api
      .adminProducts()
      .then((r) => setProducts(r.products))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  };
  useEffect(load, []);

  return (
    <>
      {error ? <p className="notice error">{error}</p> : null}
      {canEdit ? (
        <button
          className="primary"
          data-testid="add-product"
          onClick={() => setEditing({ id: null, value: { ...EMPTY_PRODUCT } })}
        >
          Add a product
        </button>
      ) : null}

      {products.length === 0 ? (
        <p className="notice info" data-testid="no-products">
          No products yet. Add one before receiving stock — every vial belongs to a product.
        </p>
      ) : null}

      {products.map((p) => (
        <div className="item" key={p.id} data-testid={`admin-product-${p.id}`}>
          <div className="top">
            <span>
              {p.name} · {p.brand}
            </span>
            <span className="badge">{p.active ? p.unit : 'inactive'}</span>
          </div>
          <div className="sub">
            step {p.minStep} · vial {p.vialSize} {p.unit} · reorder at {p.reorderLevel} ·{' '}
            {p.precision} decimal{p.precision === 1 ? '' : 's'}
          </div>
          <div className="sub">Quick amounts: {p.quickOptions.join(', ') || '—'}</div>
          {canEdit ? (
            <div className="row" style={{ marginTop: 8 }}>
              <button
                data-testid={`edit-product-${p.id}`}
                onClick={() =>
                  setEditing({
                    id: p.id,
                    value: {
                      name: p.name,
                      brand: p.brand,
                      unit: p.unit,
                      precision: p.precision,
                      minStep: p.minStep,
                      vialSize: p.vialSize,
                      quickOptions: p.quickOptions,
                      reorderLevel: p.reorderLevel,
                      active: p.active,
                    },
                  })
                }
              >
                Edit
              </button>
            </div>
          ) : null}
        </div>
      ))}

      {editing ? (
        <ProductSheet
          productId={editing.id}
          initial={editing.value}
          onClose={() => setEditing(null)}
          onDone={(m) => {
            setEditing(null);
            onNotice(m);
            load();
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function ProductSheet({
  productId,
  initial,
  onClose,
  onDone,
}: {
  productId: number | null;
  initial: ProductInput;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [form, setForm] = useState(initial);
  const [quick, setQuick] = useState(initial.quickOptions.join(', '));
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const isNew = productId === null;

  const set = <K extends keyof ProductInput>(key: K, value: ProductInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    setField(undefined);
    const parsedQuick = quick
      .split(',')
      .map((q) => q.trim())
      .filter((q) => q !== '')
      .map(Number);
    if (parsedQuick.some((q) => !Number.isFinite(q) || q <= 0)) {
      setBusy(false);
      setField('quickOptions');
      setError('Quick amounts must be positive numbers separated by commas');
      return;
    }
    try {
      const body: ProductInput = { ...form, quickOptions: parsedQuick };
      if (isNew) {
        await api.createProduct(body);
        onDone(`${body.name} added. It can now receive stock.`);
      } else {
        await api.updateProduct(productId, body);
        onDone(`${body.name} updated.`);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        setField(e.field);
      } else {
        setError('Could not save the product');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label={isNew ? 'Add a product' : 'Edit product'}>
      <div className="panel">
        <div className="row split">
          <strong>{isNew ? 'Add a product' : `Edit ${initial.name}`}</strong>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="field">
          <label htmlFor="pname">Name</label>
          <input
            id="pname"
            data-testid="product-name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="pbrand">Brand</label>
          <input
            id="pbrand"
            data-testid="product-brand"
            value={form.brand}
            onChange={(e) => set('brand', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="punit">Unit</label>
          <select
            id="punit"
            data-testid="product-unit"
            value={form.unit}
            onChange={(e) => set('unit', e.target.value)}
          >
            {['Unit', 'mg', 'mL', 'syringe'].map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="pprec">Decimal places</label>
          <select
            id="pprec"
            data-testid="product-precision"
            value={form.precision}
            disabled={!isNew}
            onChange={(e) => set('precision', Number(e.target.value))}
          >
            {[0, 1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n} ({n === 0 ? 'whole units, e.g. 20' : `e.g. ${(1.5).toFixed(n)}`})
              </option>
            ))}
          </select>
          <p className="small muted" data-testid="precision-warning">
            {isNew
              ? 'Choose carefully — this cannot be changed once the product has a ledger.'
              : 'Locked: every recorded quantity is stored against this precision.'}
          </p>
        </div>

        <div className="field">
          <label htmlFor="pstep">Smallest recordable amount</label>
          <input
            id="pstep"
            data-testid="product-minstep"
            inputMode="decimal"
            value={form.minStep}
            onChange={(e) => set('minStep', Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="psize">Amount in one vial</label>
          <input
            id="psize"
            data-testid="product-vialsize"
            inputMode="decimal"
            value={form.vialSize}
            onChange={(e) => set('vialSize', Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="pquick">Quick amounts (comma separated)</label>
          <input
            id="pquick"
            data-testid="product-quick"
            inputMode="decimal"
            placeholder="10, 20, 30, 50"
            value={quick}
            onChange={(e) => {
              setQuick(e.target.value);
              setError(null);
            }}
          />
          <p className="small muted">
            These become the big buttons staff tap. Use real doses for this product, and keep every
            one a multiple of the smallest recordable amount.
          </p>
        </div>
        <div className="field">
          <label htmlFor="preorder">Low-stock level (0 to disable)</label>
          <input
            id="preorder"
            data-testid="product-reorder"
            inputMode="decimal"
            value={form.reorderLevel}
            onChange={(e) => set('reorderLevel', Number(e.target.value))}
          />
        </div>

        {!isNew ? (
          <div className="field">
            <label htmlFor="pactive">Status</label>
            <select
              id="pactive"
              data-testid="product-active"
              value={form.active === false ? 'no' : 'yes'}
              onChange={(e) => set('active', e.target.value === 'yes')}
            >
              <option value="yes">Active</option>
              <option value="no">Inactive (hidden from staff)</option>
            </select>
          </div>
        ) : null}

        {error ? (
          <p className="notice error" role="alert" data-testid="product-error">
            {error}
            {field ? ` (${field})` : ''}
          </p>
        ) : null}

        <button
          className="primary"
          data-testid="product-submit"
          disabled={busy || !form.name.trim() || !form.brand.trim()}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : isNew ? 'Add product' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function People({
  currentUserId,
  onNotice,
}: {
  currentUserId: number;
  onNotice: (m: string) => void;
}) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () => {
    api
      .adminUsers()
      .then((r) => setUsers(r.users))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  };
  useEffect(load, []);

  const toggle = async (user: AdminUser) => {
    setBusyId(user.id);
    setError(null);
    try {
      await api.updateUser(user.id, { active: !user.active });
      onNotice(
        user.active
          ? `${user.displayName} deactivated. Any open session ends immediately.`
          : `${user.displayName} reactivated.`,
      );
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update the account');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {error ? <p className="notice error">{error}</p> : null}
      <button className="primary" data-testid="add-user" onClick={() => setAdding(true)}>
        Add a person
      </button>

      {users.map((u) => (
        <div className="item" key={u.id} data-testid={`admin-user-${u.id}`}>
          <div className="top">
            <span>{u.displayName}</span>
            <span className={`badge ${u.active ? 'approved' : 'rejected'}`}>
              {u.active ? u.role : 'INACTIVE'}
            </span>
          </div>
          <div className="sub">
            signs in as <strong>{u.code}</strong>
            {u.id === currentUserId ? ' · this is you' : ''}
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            <button data-testid={`reset-pin-${u.id}`} onClick={() => setResetting(u)}>
              Reset PIN
            </button>
            <button
              className={u.active ? 'danger' : ''}
              data-testid={`toggle-user-${u.id}`}
              disabled={busyId === u.id || u.id === currentUserId}
              onClick={() => void toggle(u)}
            >
              {u.active ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        </div>
      ))}

      {adding ? (
        <UserSheet
          onClose={() => setAdding(false)}
          onDone={(m) => {
            setAdding(false);
            onNotice(m);
            load();
          }}
        />
      ) : null}

      {resetting ? (
        <ResetPinSheet
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={(m) => {
            setResetting(null);
            onNotice(m);
            load();
          }}
        />
      ) : null}
    </>
  );
}

function UserSheet({ onClose, onDone }: { onClose: () => void; onDone: (m: string) => void }) {
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('STAFF');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.createUser({ code: code.trim(), displayName: displayName.trim(), role, pin });
      onDone(`${displayName.trim()} can now sign in as "${code.trim()}".`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create the account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label="Add a person">
      <div className="panel">
        <div className="row split">
          <strong>Add a person</strong>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="field">
          <label htmlFor="uname">Display name</label>
          <input
            id="uname"
            data-testid="user-name"
            placeholder="Ava Chen, RN"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <p className="small muted">This is what appears on the sign-in list and the ledger.</p>
        </div>
        <div className="field">
          <label htmlFor="ucode">Sign-in code</label>
          <input
            id="ucode"
            data-testid="user-code"
            placeholder="ava"
            autoCapitalize="none"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="urole">Role</label>
          <select
            id="urole"
            data-testid="user-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            <option value="STAFF">Staff — records use and waste only</option>
            <option value="MANAGER">Manager — stock, counts, approvals, ledger</option>
            <option value="OWNER">Owner — everything, including this screen</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="upin">PIN (4–10 digits)</label>
          <input
            id="upin"
            data-testid="user-pin"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </div>
        {error ? (
          <p className="notice error" role="alert" data-testid="user-error">
            {error}
          </p>
        ) : null}
        <button
          className="primary"
          data-testid="user-submit"
          disabled={busy || !code.trim() || !displayName.trim() || pin.length < 4}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : 'Add person'}
        </button>
      </div>
    </div>
  );
}

function ResetPinSheet({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateUser(user.id, { pin });
      onDone(`PIN reset for ${user.displayName}. Their open sessions have ended.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reset the PIN');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label="Reset PIN">
      <div className="panel">
        <div className="row split">
          <strong>Reset PIN for {user.displayName}</strong>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <p className="small muted">
          The old PIN stops working straight away and any tablet still signed in as{' '}
          {user.displayName} is signed out.
        </p>
        <div className="field">
          <label htmlFor="rpin">New PIN (4–10 digits)</label>
          <input
            id="rpin"
            data-testid="reset-pin-value"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </div>
        {error ? (
          <p className="notice error" role="alert" data-testid="reset-error">
            {error}
          </p>
        ) : null}
        <button
          className="primary"
          data-testid="reset-submit"
          disabled={busy || pin.length < 4}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : 'Reset PIN'}
        </button>
      </div>
    </div>
  );
}
